import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Loader2, Package, Zap } from 'lucide-react';
import { useAppSelector } from '../../../redux/hooks';
import type { PortfolioSite } from '../../../redux/api/portfolioApi';
import { useLazyGetComponentListQuery, type ComponentListItem } from '../../../redux/api/entityApi';
import { getCategoryInfo } from '../../../services/portfolioComponentService';

const CATEGORY_ORDER = ['module', 'inverter'];
const SITES_TO_LOAD = 10;

interface CategorySummary {
  category: string;
  totalCount: number;
  siteCount: number;
  models: ModelSummary[];
}

interface ModelSummary {
  id: string;
  model: string;
  manufacturer: string;
  count: number;
  siteCount: number;
}

function extractFromProperties(props: Record<string, unknown> | null): { manufacturer: string; model: string } {
  if (!props) return { manufacturer: 'Unknown', model: 'Unknown' };
  const manufacturer = (props.manufacturer || props.Manufacturer || props.make || props.brand || 'Unknown') as string;
  const model = (props.model || props.Model || props.part_number || 'Unknown') as string;
  return { manufacturer, model };
}

function aggregateComponents(
  allItems: { site: PortfolioSite; items: ComponentListItem[] }[]
): Map<string, CategorySummary> {
  const catMap = new Map<string, {
    totalCount: number;
    siteIds: Set<string>;
    models: Map<string, { model: string; manufacturer: string; count: number; siteIds: Set<string> }>;
  }>();

  for (const { site, items } of allItems) {
    const siteId = site.site_id || site.id || '';
    for (const item of items) {
      const componentType = item.component_type || 'unknown';
      const { category } = getCategoryInfo(componentType);
      if (!CATEGORY_ORDER.includes(category)) continue;

      if (!catMap.has(category)) {
        catMap.set(category, { totalCount: 0, siteIds: new Set(), models: new Map() });
      }
      const cat = catMap.get(category)!;
      cat.totalCount++;
      cat.siteIds.add(siteId);

      const { manufacturer, model } = extractFromProperties(item.properties);
      const key = `${model}::${componentType}`.toLowerCase();
      if (!cat.models.has(key)) {
        cat.models.set(key, { model, manufacturer, count: 0, siteIds: new Set() });
      }
      const m = cat.models.get(key)!;
      m.count++;
      m.siteIds.add(siteId);
    }
  }

  const result = new Map<string, CategorySummary>();
  for (const [category, data] of catMap) {
    const models = Array.from(data.models.entries())
      .map(([id, m]) => ({ id, model: m.model, manufacturer: m.manufacturer, count: m.count, siteCount: m.siteIds.size }))
      .sort((a, b) => b.count - a.count);
    result.set(category, {
      category,
      totalCount: data.totalCount,
      siteCount: data.siteIds.size,
      models,
    });
  }
  return result;
}

interface ComponentsSummaryModuleProps {
  sites: PortfolioSite[];
}

const ComponentsSummaryModule: React.FC<ComponentsSummaryModuleProps> = ({ sites }) => {
  const selectedPortfolioId = useAppSelector(state => state.portfolio.selectedPortfolioId);
  const [fetchComponentList] = useLazyGetComponentListQuery();

  const [summaries, setSummaries] = useState<Map<string, CategorySummary>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ current: 0, total: 0 });
  const [selectedCategory, setSelectedCategory] = useState<string>('module');
  const loadedKeyRef = useRef('');
  const loadingRef = useRef(false);

  const sitesToLoad = useMemo(() => sites.slice(0, SITES_TO_LOAD), [sites]);
  const siteKey = useMemo(
    () => sitesToLoad.map(s => s.site_id || s.id || '').sort().join(','),
    [sitesToLoad]
  );

  useEffect(() => {
    if (!siteKey || loadingRef.current || loadedKeyRef.current === siteKey) return;

    loadingRef.current = true;
    setIsLoading(true);
    setLoadProgress({ current: 0, total: sitesToLoad.length });

    const load = async () => {
      const allData: { site: PortfolioSite; items: ComponentListItem[] }[] = [];
      const batchSize = 3;

      for (let i = 0; i < sitesToLoad.length; i += batchSize) {
        const batch = sitesToLoad.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (site) => {
            const siteId = site.site_id || site.id || '';
            const portfolioId = site.portfolio_id || selectedPortfolioId || '';
            if (!siteId || !portfolioId) return null;
            try {
              const resp = await fetchComponentList({
                portfolio_id: portfolioId,
                site_id: siteId,
                exclude_modules: false,
              }).unwrap();
              return { site, items: resp.items || [] };
            } catch {
              return null;
            }
          })
        );
        for (const r of results) {
          if (r.status === 'fulfilled' && r.value) allData.push(r.value);
        }
        setLoadProgress({ current: Math.min(i + batchSize, sitesToLoad.length), total: sitesToLoad.length });
      }

      if (allData.length > 0) {
        setSummaries(aggregateComponents(allData));
      }
      loadedKeyRef.current = siteKey;
      setIsLoading(false);
      loadingRef.current = false;
    };

    load();
  }, [siteKey, sitesToLoad, selectedPortfolioId, fetchComponentList]);

  const activeSummary = summaries.get(selectedCategory) || null;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-engramiq-green mb-2" />
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          Loading components ({loadProgress.current}/{loadProgress.total} sites)
        </p>
        <div className="w-40 h-1 rounded-full mt-2 overflow-hidden" style={{ background: 'var(--color-bg-tertiary)' }}>
          <div
            className="h-full bg-engramiq-green transition-all duration-300 rounded-full"
            style={{ width: loadProgress.total > 0 ? `${(loadProgress.current / loadProgress.total) * 100}%` : '0%' }}
          />
        </div>
      </div>
    );
  }

  if (summaries.size === 0) {
    return (
      <div className="flex flex-col items-center py-6" style={{ color: 'var(--color-text-muted)' }}>
        <Package className="w-8 h-8 mb-2 opacity-30" />
        <p className="text-xs">No component data available</p>
      </div>
    );
  }

  return (
    <div>
      {/* Category toggle */}
      <div className="flex gap-1.5 mb-4">
        {CATEGORY_ORDER.map((cat) => {
          const summary = summaries.get(cat);
          const count = summary?.totalCount || 0;
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all"
              style={{
                background: isActive
                  ? (cat === 'module' ? '#22c55e' : '#eab308')
                  : 'var(--color-bg-tertiary)',
                color: isActive ? '#fff' : 'var(--color-text-secondary)',
              }}
            >
              {cat === 'module' ? <Package className="w-3.5 h-3.5" /> : <Zap className="w-3.5 h-3.5" />}
              <span className="capitalize">{cat}s</span>
              {count > 0 && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={{ background: isActive ? 'rgba(255,255,255,0.2)' : 'var(--color-overlay-light)' }}
                >
                  {count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active category detail */}
      {activeSummary ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {activeSummary.models.length} model{activeSummary.models.length !== 1 ? 's' : ''} across {activeSummary.siteCount} site{activeSummary.siteCount !== 1 ? 's' : ''}
            </span>
            <span className="text-sm font-bold text-engramiq-green">
              {activeSummary.totalCount.toLocaleString()}
            </span>
          </div>

          <div className="space-y-0.5">
            {activeSummary.models.slice(0, 6).map((model) => (
              <div
                key={model.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded transition-colors hover:bg-[var(--color-surface-hover)]"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-xs block truncate" style={{ color: 'var(--color-text-primary)' }}>
                    {model.model}
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                    {model.manufacturer} &middot; {model.siteCount} site{model.siteCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <span className="text-xs font-medium text-engramiq-green shrink-0">
                  {model.count.toLocaleString()}
                </span>
              </div>
            ))}
            {activeSummary.models.length > 6 && (
              <p className="text-[10px] text-center pt-1" style={{ color: 'var(--color-text-muted)' }}>
                + {activeSummary.models.length - 6} more models
              </p>
            )}
          </div>

          {sites.length > SITES_TO_LOAD && (
            <p className="text-[10px] mt-3 text-center" style={{ color: 'var(--color-text-muted)' }}>
              Based on first {SITES_TO_LOAD} of {sites.length} sites
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
          No {selectedCategory} data
        </p>
      )}
    </div>
  );
};

export default ComponentsSummaryModule;
